import { uploadToPresignedUrl } from "shared/api/uploadToPresignedUrl"
import { Logger } from "shared/lib/Logger"
import { generateUUID } from "shared/lib/generateUUID"
import { useEvent } from "shared/lib/useEvent"
import { ApiResponse } from "shared/types/ApiResponse"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import { ProjectsAPIType } from "shared/types/Project"

import { AttachmentFragment } from "../graphql/fragments.generated"
import {
  useCreateAttachmentMutation,
  useUpdateAttachmentMutation
} from "../graphql/mutations.generated"
import {
  AttachmentsDocument,
  useAttachmentsLazyQuery
} from "../graphql/project-attachments.queries.generated"
import { AttachmentCreateError } from "../types/errors/AttachmentCreateError"

export enum AttachmentErrorCode {
  CREATE_FAILED = "ATTACHMENT_CREATE_ERROR",
  REPLACE_FAILED = "ATTACHMENT_REPLACE_ERROR",
  UPLOAD_FAILED = "ATTACHMENT_UPLOAD_ERROR"
}

export function useProjectAttachmentUpload() {
  const [mutate] = useCreateAttachmentMutation()
  const [updateMutate] = useUpdateAttachmentMutation()
  const [refetchAttachments] = useAttachmentsLazyQuery()

  const createAttachment = useEvent(
    async (
      file: File,
      options: { projectID: string; category: string }
    ): Promise<ApiResponse<AttachmentFragment>> => {
      const response = await mutate({
        variables: {
          id: options.projectID,
          type: ProjectsAPIType,
          input: {
            id: generateUUID(),
            fileName: file.name,
            title: file.name,
            category: options.category,
            position: 0
          }
        },
        refetchQueries: [
          {
            query: AttachmentsDocument,
            variables: { id: options.projectID, type: ProjectsAPIType }
          }
        ]
      })

      const attachment = response.data?.attachment

      if (response.errors || !attachment?.writeUrl) {
        const error = new AttachmentCreateError(
          "Failed to create attachment record"
        )
        Logger.error(error, "useProjectAttachmentUpload", {
          projectID: options.projectID,
          errors: response.errors
        })
        return {
          data: null,
          errors: [
            { message: error.message, code: AttachmentErrorCode.CREATE_FAILED }
          ]
        }
      }

      const uploadError = await uploadToPresignedUrl(
        attachment.writeUrl,
        file
      ).catch(error => {
        Logger.error(error, "useProjectAttachmentUpload", {
          projectID: options.projectID,
          attachmentId: attachment.id
        })
        return error
      })

      await refetchAttachments({
        variables: {
          id: options.projectID,
          type: ProjectsAPIType
        },
        fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
      })

      if (uploadError) {
        return {
          data: null,
          errors: [
            {
              message: uploadError.message,
              code: AttachmentErrorCode.UPLOAD_FAILED
            }
          ]
        }
      }

      return { data: attachment }
    }
  )

  const createAttachments = useEvent(
    async (
      files: File[],
      options: { projectID: string; category: string }
    ): Promise<ApiResponse<AttachmentFragment[]>> => {
      const results = await Promise.allSettled(
        files.map(file => createAttachment(file, options))
      )

      const successes = results.reduce<AttachmentFragment[]>((acc, r) => {
        if (r.status === "fulfilled" && r.value.data) {
          acc.push(r.value.data)
        }
        return acc
      }, [])

      const failureCount = results.filter(
        r => r.status === "rejected" || r.value.errors?.length
      ).length

      return {
        data: successes,
        errors: failureCount
          ? [{ message: `${failureCount} of ${results.length} uploads failed` }]
          : undefined
      }
    }
  )

  const replaceAttachment = useEvent(
    async (
      attachment: AttachmentFragment,
      file: File,
      projectID: string
    ): Promise<ApiResponse<AttachmentFragment>> => {
      const response = await updateMutate({
        variables: {
          id: attachment.id,
          input: {
            id: attachment.id,
            fileName: file.name,
            title: file.name
          }
        },
        refetchQueries: [
          {
            query: AttachmentsDocument,
            variables: { id: projectID, type: ProjectsAPIType }
          }
        ]
      })

      const updated = response.data?.attachment

      if (response.errors || !updated?.writeUrl) {
        const error = new AttachmentCreateError(
          "Failed to update attachment record"
        )
        Logger.error(error, "useProjectAttachmentUpload", {
          attachmentId: attachment.id,
          errors: response.errors
        })
        return {
          data: null,
          errors: [
            { message: error.message, code: AttachmentErrorCode.REPLACE_FAILED }
          ]
        }
      }

      const uploadError = await uploadToPresignedUrl(
        updated.writeUrl,
        file
      ).catch(error => {
        Logger.error(error, "useProjectAttachmentUpload", {
          attachmentId: attachment.id
        })
        return error
      })

      if (uploadError) {
        return {
          data: null,
          errors: [
            {
              message: uploadError.message,
              code: AttachmentErrorCode.UPLOAD_FAILED
            }
          ]
        }
      }

      return { data: updated }
    }
  )

  return { createAttachment, createAttachments, replaceAttachment }
}
